import * as React from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { debounce, type DebouncedFunc } from 'lodash'
import baseApi, { type QueryTagTypes } from '@core/api'
import { useAppDispatch } from '@core/store'
import type {
    TopUpCompleteEvent,
    TopUpPendingEvent,
    TopUpProviderProps,
} from './TopUpContext.types'
import { useNotification } from '@core/hooks'
import { emitter, SendPostMessage, useTheme } from '@core/utils'
import { POST_MESSAGE_EVENT } from '@core/constants'
import TopUpContext from './TopUpContext'

const TopUpProvider: React.FC<TopUpProviderProps> = ({ children }) => {
    const theme = useTheme().colors

    const { t } = useTranslation()
    const { alert } = useNotification()

    const navigate = useNavigate()
    const dispatch = useAppDispatch()

    const pendingAlert = React.useRef<DebouncedFunc<() => void>>(undefined)

    const update = React.useCallback(
        (updateTags: QueryTagTypes) => {
            dispatch(baseApi.util.invalidateTags(updateTags))
        },
        [dispatch],
    )

    const navigation = React.useCallback(
        (event: TopUpPendingEvent) => {
            if (event?.redirect)
                return navigate(event.redirect.path, {
                    replace: true,
                    state: {
                        ...event.redirect,
                    },
                })

            navigate('/', {
                state: { walletId: event.walletId },
                replace: true,
            })
        },
        [navigate],
    )

    const complete = React.useCallback(
        async (event: TopUpCompleteEvent) => {
            pendingAlert.current?.cancel()

            update([
                'WalletDetail',
                'BalanceHistoryList',
                'DepositCampaignDetail',
            ])

            debounce(
                () =>
                    alert({
                        icon: 'check-circle',
                        iconColor: theme.semantic.content.success,
                        title: t('context.TopUpProvider.alert_success_title'),
                        message: t(
                            'context.TopUpProvider.toast_success_message',
                        ),
                        duration: 10000,
                        actions: [
                            {
                                children: t(
                                    'context.TopUpProvider.alert_success_action',
                                ),
                            },
                        ],
                    }),
                750,
            )()

            SendPostMessage(
                JSON.stringify({
                    success: true,
                    event: POST_MESSAGE_EVENT.DEPOSIT.COMPLETE,
                }),
                POST_MESSAGE_EVENT.DEPOSIT.COMPLETE,
            )

            navigation(event)
        },
        [alert, navigation, t, theme, update],
    )

    const failed = React.useCallback(
        async (event: TopUpCompleteEvent) => {
            pendingAlert.current?.cancel()

            SendPostMessage(
                JSON.stringify({
                    success: false,
                    event: POST_MESSAGE_EVENT.DEPOSIT.CANCEL_USER,
                }),
                POST_MESSAGE_EVENT.DEPOSIT.CANCEL_USER,
            )

            navigation(event)
        },
        [navigation],
    )

    const pending = React.useCallback(
        (event: TopUpPendingEvent) => {
            update([
                'WalletDetail',
                'BalanceHistoryList',
                'DepositCampaignDetail',
            ])

            pendingAlert.current = debounce(
                () =>
                    alert({
                        icon: 'clock',
                        iconColor: theme.semantic.content.info,
                        title: t('context.TopUpProvider.alert_pending_title'),
                        message: t(
                            'context.TopUpProvider.alert_pending_message',
                        ),
                        duration: 10000,
                        actions: [
                            {
                                children: t(
                                    'context.TopUpProvider.alert_success_action',
                                ),
                            },
                        ],
                    }),
                750,
            )
            pendingAlert.current()

            navigation(event)
        },
        [alert, navigation, t, theme, update],
    )

    React.useEffect(() => {
        emitter.on('TOPUP_COMPLETE', complete)
        emitter.on('TOPUP_UPDATE', update)
        emitter.on('TOPUP-FAILED', failed)
        emitter.on('TOPUP_PENDING', pending)

        return () => {
            emitter.off('TOPUP_COMPLETE', complete)
            emitter.off('TOPUP_UPDATE', update)
            emitter.off('TOPUP-FAILED', failed)
            emitter.off('TOPUP_PENDING', pending)
        }
    }, [complete, failed, pending, update])

    return (
        <TopUpContext.Provider value={undefined}>
            {children}
        </TopUpContext.Provider>
    )
}

export default TopUpProvider
